import { useNavigate } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';

import ListPage from '../../components/ListPage';

import Copy from '../../components/ListPage/components/ActionsToolbar/components/Copy';
import Delete from '../../components/ListPage/components/ActionsToolbar/components/Delete';
import ForceComplete from '../../components/ListPage/components/ActionsToolbar/components/ForceComplete';
import Pause from './components/Pause';
import Rename from '../../components/ListPage/components/ActionsToolbar/components/Rename';
import Resume from './components/Resume';
import Share from '../../components/ListPage/components/ActionsToolbar/components/Share';

import {
  deleteInstantSurvey,
  forceCompleteInstantSurvey,
  patchInstantSurvey,
} from '@api/instant_survey';
import {
  completedSurveyStatuses,
  deleteSurveys,
  pausedEditableSurveyStatuses,
  pausedReadOnlySurveyStatuses,
  readSurveyDrafts,
  readSurveys,
  surveyPageSize,
  surveyStatus,
  updateSurveyTitle,
  shareInstantSurveyWithClientGroup,
  shareInstantSurveyWithUsers,
} from '@api/surveys';

import { getFilterOptions, getItemColumns } from './utilities/helpers.js';
import { GATES } from '@constants';

// Keep in sync with ALCHEMER_FILTER_OPTIONS / CALYPSO_FILTER_OPTIONS in ./utilities/helpers.js
const ALCHEMER_LAUNCHED_TAGS = ['active', 'issuing', 'complete', 'in_review'];
const CALYPSO_LAUNCHED_TAGS = ['fielded', 'in_review', 'met_sample', 'completed'];
const SEPARATE_DRAFTS_LAUNCHED_PAGE_SIZE = 10;

const mergeLaunchedTags = (tags = [], useCalypsoStatus = false) => {
  const launched = useCalypsoStatus ? CALYPSO_LAUNCHED_TAGS : ALCHEMER_LAUNCHED_TAGS;
  const known = new Set(['draft', ...launched]);
  // ListPage emits filter values with the `is_` prefix intact; strip it so the lookup hits.
  const normalized = tags.map((tag) => tag.replace(/^is_/, ''));
  const withoutDraft = normalized.filter((tag) => tag !== 'draft');
  const hasStatusTag = withoutDraft.some((tag) => known.has(tag));
  return hasStatusTag ? withoutDraft : [...withoutDraft, ...launched];
};

const Surveys = () => {
  const navigate = useNavigate();
  const { client } = useStatsigClient();
  const useCalypsoStatus = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);
  const isSeparateDrafts = client.checkGate(GATES.MY_SURVEYS_SEPARATE_DRAFTS);

  const navigateToDraft = ({ atlasInstantSurveyRef, id, instantSurveyTypeId }) => {
    const params = { id };
    if (atlasInstantSurveyRef) params.ref = atlasInstantSurveyRef;
    navigate({
      pathname: `/dashboard/create-survey/${instantSurveyTypeId}`,
      search: new URLSearchParams(params).toString(),
    });
  };

  const expandSurveyRow = ({
    atlasInstantSurveyRef,
    id,
    instantSurveyTypeId,
    isPaused,
    status,
  }) => {
    if (
      status === surveyStatus.DRAFT ||
      (useCalypsoStatus &&
        isPaused &&
        (pausedEditableSurveyStatuses.includes(status) ||
          pausedReadOnlySurveyStatuses.includes(status)))
    ) {
      navigateToDraft({ atlasInstantSurveyRef, id, instantSurveyTypeId });
      return;
    }

    if (completedSurveyStatuses.includes(status)) {
      navigate({ pathname: `/dashboard/survey/${id}` });
      return;
    }
  };

  const handleDeleteSurveys = async (surveys) => {
    return await Promise.all([
      deleteSurveys({ ids: surveys.map(({ id }) => id) }),
      ...surveys
        .filter(({ atlasInstantSurveyRef }) => atlasInstantSurveyRef)
        .map(({ atlasInstantSurveyRef }) => deleteInstantSurvey({ id: atlasInstantSurveyRef })),
    ]);
  };

  const handleRenameSurvey = async ({ name, row: survey }) => {
    return await Promise.all([
      updateSurveyTitle({ id: survey.id, name }),
      ...(survey.atlasInstantSurveyRef
        ? [patchInstantSurvey({ data: { title: name }, id: survey.atlasInstantSurveyRef })]
        : []),
    ]);
  };

  const handleForceCompleteSurvey = async (survey) => {
    return await forceCompleteInstantSurvey({ atlasSurveyRef: survey.atlasInstantSurveyRef });
  };

  const itemActions = [
    <Pause key="pause-item" />,
    <Resume key="resume-item" />,
    <ForceComplete
      handleForceCompleteSurvey={handleForceCompleteSurvey}
      key="force-complete-item"
    />,
    <Copy key="copy-item" redirect="/dashboard/create-survey" type="Survey" />,
    <Rename key="rename-item" type="Survey" updateName={handleRenameSurvey} />,
    <Share
      hasAllColleagues
      hasFindColleagues
      hasSharePublicly
      hideOnMultiSelect
      isSurvey
      key="share-item"
      shareWithClient={shareInstantSurveyWithClientGroup}
      shareWithUsers={shareInstantSurveyWithUsers}
      type="survey"
    />,
    <Delete deleteItems={handleDeleteSurveys} key="delete-item" />,
  ];

  const draftActions = [
    <Copy key="copy-draft" redirect="/dashboard/create-survey" type="Survey" />,
    <Rename key="rename-draft" type="Survey" updateName={handleRenameSurvey} />,
    <Share
      hasAllColleagues
      hasFindColleagues
      hasSharePublicly
      hideOnMultiSelect
      isSurvey
      key="share-draft"
      shareWithClient={shareInstantSurveyWithClientGroup}
      shareWithUsers={shareInstantSurveyWithUsers}
      type="survey"
    />,
    <Delete deleteItems={handleDeleteSurveys} key="delete-draft" />,
  ];

  const readLaunchedSurveys = (params) =>
    readSurveys({ ...params, tags: mergeLaunchedTags(params?.tags, useCalypsoStatus) });

  const columns = getItemColumns(useCalypsoStatus);

  return (
    <ListPage
      filterOptions={getFilterOptions(useCalypsoStatus, { excludeDraft: isSeparateDrafts })}
      folderActions={isSeparateDrafts ? draftActions : undefined}
      folderColumns={isSeparateDrafts ? columns : undefined}
      folderEmptyMessage={
        isSeparateDrafts ? (
          <>
            <h3>No draft surveys yet.</h3>
            <p>Drafts you start will appear here — pick up where you left off any time.</p>
          </>
        ) : undefined
      }
      folderSectionTitle={isSeparateDrafts ? 'Drafts' : undefined}
      folderSize={5}
      folderType={isSeparateDrafts ? 'Survey' : undefined}
      itemActions={itemActions}
      itemColumns={columns}
      itemEmptyMessage={
        isSeparateDrafts ? (
          <>
            <h3>No launched surveys yet.</h3>
            <p>Surveys you&apos;ve launched will appear here.</p>
          </>
        ) : undefined
      }
      itemSectionTitle={isSeparateDrafts ? 'Launched' : undefined}
      itemSize={isSeparateDrafts ? SEPARATE_DRAFTS_LAUNCHED_PAGE_SIZE : surveyPageSize}
      onCreate={() => navigate('/dashboard/verifiedvoices/instant-surveys')}
      onExpandItemRow={expandSurveyRow}
      onFolderRowClick={isSeparateDrafts ? navigateToDraft : undefined}
      readFolders={isSeparateDrafts ? readSurveyDrafts : undefined}
      readItems={isSeparateDrafts ? readLaunchedSurveys : readSurveys}
      shouldHideFolders={
        isSeparateDrafts
          ? (activeFilters) => activeFilters.some((option) => option?.value?.includes('_item'))
          : undefined
      }
      type="Survey"
    />
  );
};

export default Surveys;
